import { IdCardImageTypes } from "./identity-reports";

export enum DOCUMENT_STATUS {
  PENDING = 'PENDING',
  PROVIDED = 'PROVIDED',
  VALIDATING_SIMPLE = 'VALIDATING_SIMPLE',
  VALIDATING_COMPLEX = 'VALIDATING_COMPLEX',
  INVALID = 'INVALID',
  VALID = 'VALID',
}

export enum ID_VERIFICATION_STATUS {
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  MANUAL = 'MANUAL',
}

// export enum DOCUMENT_TYPE {
//   ID_FRONT = 'ID_FRONT',
//   ID_BACK  = 'ID_BACK',
//   SELFIE   = 'SELFIE',
// }

// ── Verification detail ────────────────────────────────────────────────

export interface VerificationDetail {
  idCardFront: number | null;
  idCardBack: number | null;
  selfie: number | null;
  idFrontStatus: string;
  idBackStatus: string;
  selfieStatus: string;
  idFrontReason: string | null;
  idBackReason: string | null;
  selfieReason: string | null;
}

// ── Slot config ────────────────────────────────────────────────────────

export interface SlotConfig {
  label: string;
  apiType: IdCardImageTypes;
  statusKey: keyof VerificationDetail;
  reasonKey: keyof VerificationDetail;
  docKey: keyof VerificationDetail;
}

export const SLOT_CONFIGS = {
  front: {
    label: 'ID Front',
    apiType: IdCardImageTypes.ID_FRONT,
    statusKey: 'idFrontStatus',
    reasonKey: 'idFrontReason',
    docKey: 'idCardFront',
  },
  back: {
    label: 'ID Back',
    apiType: IdCardImageTypes.ID_BACK,
    statusKey: 'idBackStatus',
    reasonKey: 'idBackReason',
    docKey: 'idCardBack',
  },
  selfie: {
    label: 'Selfie',
    apiType: IdCardImageTypes.SELFIE,
    statusKey: 'selfieStatus',
    reasonKey: 'selfieReason',
    docKey: 'selfie',
  },
} as const satisfies Record<string, SlotConfig>;

export type SlotKey = keyof typeof SLOT_CONFIGS;
export const SLOT_KEYS = Object.keys(SLOT_CONFIGS) as SlotKey[];

// ── Status parsing ─────────────────────────────────────────────────────

export interface ParsedDocumentStatus {
  /** The recognised base status. */
  base: DOCUMENT_STATUS;
  /** Everything after the base prefix, e.g. 'PA_KATA' from 'INVALID_PA_KATA'. */
  reason: string | null;
  /** The original unmodified value received from the API. */
  raw: string;
}

// Sorted longest-first so 'VALIDATING_COMPLEX' is never shadowed by a
// shorter prefix that shares a leading segment.
const BASE_STATUSES = (Object.values(DOCUMENT_STATUS) as string[])
  .sort((a, b) => b.length - a.length);

/**
 * Splits a raw API status string into a known base status and an optional
 * reason suffix.
 *
 * Examples:
 *   'VALID'           → { base: VALID,   reason: null }
 *   'INVALID_PA_KATA' → { base: INVALID, reason: 'PA_KATA' }
 *   'UNKNOWN_THING'   → { base: INVALID, reason: 'UNKNOWN_THING' }  ← fallback
 */
export function parseDocumentStatus(raw: string): ParsedDocumentStatus {
  for (const base of BASE_STATUSES) {

    if (!raw) {
      return { base: DOCUMENT_STATUS.PENDING, reason: null, raw };
    }

    if (raw === base) {
      return { base: base as DOCUMENT_STATUS, reason: null, raw };
    }

    if (raw.startsWith(base + '_')) {
      const reason = raw.slice(base.length + 1);
      return { base: base as DOCUMENT_STATUS, reason, raw };
    }
  }

  return { base: DOCUMENT_STATUS.INVALID, reason: raw, raw };
}

/** Formats a reason token into a human-readable label.
 *  'PA_KATA' → 'Pa Kata'
 */
export function formatDocumentReason(reason: string): string {
  return reason
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}